import React, { useState } from 'react';
import axios from 'axios';
import {
  FEEDBACK_CATEGORIES,
  FEEDBACK_CATEGORY_LABELS,
  MAX_LENGTHS,
  validateFeedbackMessage,
} from '../utils/validation';

const CATEGORY_META = {
  bug: {
    color: 'border-red-700/50 bg-red-900/20 text-red-400',
    selectedColor: 'border-red-500 bg-red-900/40 ring-2 ring-red-500',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  feature_request: {
    color: 'border-blue-700/50 bg-blue-900/20 text-blue-400',
    selectedColor: 'border-blue-500 bg-blue-900/40 ring-2 ring-blue-500',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      </svg>
    ),
  },
  usability: {
    color: 'border-yellow-700/50 bg-yellow-900/20 text-yellow-400',
    selectedColor: 'border-yellow-500 bg-yellow-900/40 ring-2 ring-yellow-500',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
      </svg>
    ),
  },
  general: {
    color: 'border-[#243B55] bg-[#0D1B2A] text-[#E5C07B]',
    selectedColor: 'border-[#00C2A8] bg-[#00C2A8]/20 ring-2 ring-[#00C2A8] text-[#00C2A8]',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
      </svg>
    ),
  },
};

function StarRating({ rating, hoveredStar, onRate, onHover, onLeave }) {
  return (
    <div className="flex items-center space-x-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onRate(star)}
          onMouseEnter={() => onHover(star)}
          onMouseLeave={onLeave}
          className="focus:outline-none transition-colors"
          aria-label={`Rate ${star} star${star > 1 ? 's' : ''}`}
        >
          <svg
            className={`w-8 h-8 ${
              star <= (hoveredStar || rating)
                ? 'text-yellow-400'
                : 'text-[#243B55]'
            }`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        </button>
      ))}
      {rating > 0 && (
        <button
          type="button"
          onClick={() => onRate(0)}
          className="ml-2 text-xs text-[#E5C07B] hover:text-white"
        >
          Clear
        </button>
      )}
    </div>
  );
}

export default function FeedbackModal({ isOpen, onClose }) {
  const [category, setCategory] = useState('');
  const [rating, setRating] = useState(0);
  const [hoveredStar, setHoveredStar] = useState(0);
  const [message, setMessage] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async () => {
    const errors = {};
    if (!category) errors.category = 'Please select a category';
    const msgErr = validateFeedbackMessage(message);
    if (msgErr) errors.message = msgErr;
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setSubmitting(true);
    setError('');
    try {
      await axios.post('/api/feedback', {
        category,
        rating: rating || null,
        message: message.trim(),
        page_url: window.location.pathname,
      });
      setSubmitted(true);
    } catch (err) {
      if (err.response?.status === 422 && err.response?.data?.errors) {
        const serverErrors = {};
        err.response.data.errors.forEach((e) => {
          serverErrors[e.field] = e.message;
        });
        setFieldErrors(serverErrors);
      } else {
        setError(err.response?.data?.detail || err.response?.data?.message || 'Failed to submit feedback. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setCategory('');
    setRating(0);
    setHoveredStar(0);
    setMessage('');
    setFieldErrors({});
    setSubmitted(false);
    setError('');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-16 px-4">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-60" onClick={handleClose} />

      {/* Modal */}
      <div className="relative bg-[#1A2E45] border border-[#243B55] rounded-xl shadow-2xl w-full max-w-lg p-6 z-10">
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-[#E5C07B] hover:text-white transition-colors"
          aria-label="Close feedback form"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {submitted ? (
          /* Success state */
          <div className="text-center py-8">
            <div className="mx-auto w-16 h-16 bg-green-900/30 rounded-full flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">Thank you!</h3>
            <p className="text-[#E5C07B] mb-6">
              Your feedback has been submitted and helps us improve Omnio View.
            </p>
            <button
              onClick={handleClose}
              className="bg-[#00C2A8] text-[#0D1B2A] font-semibold px-6 py-2 rounded-lg hover:bg-[#009987] transition-colors"
            >
              Close
            </button>
          </div>
        ) : (
          /* Feedback form */
          <>
            <h2 className="text-xl font-semibold text-white mb-1">Share Your Feedback</h2>
            <p className="text-sm text-[#E5C07B] mb-5">
              Help us improve Omnio View by sharing your thoughts.
            </p>

            {error && (
              <div role="alert" className="mb-4 p-3 bg-red-900/30 border border-red-700/50 rounded-lg text-sm text-red-400">
                {error}
              </div>
            )}

            {/* Category selector */}
            <label className="block text-sm font-medium text-[#E5C07B] mb-2">
              Category <span className="text-red-400">*</span>
            </label>
            <div className="grid grid-cols-2 gap-2 mb-1">
              {FEEDBACK_CATEGORIES.map((cat) => {
                const meta = CATEGORY_META[cat];
                const isSelected = category === cat;
                return (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => {
                      setCategory(cat);
                      setFieldErrors((prev) => ({ ...prev, category: undefined }));
                    }}
                    className={`flex items-center space-x-2 p-3 rounded-lg border-2 transition-all text-sm font-medium ${
                      isSelected ? meta.selectedColor : meta.color + ' hover:opacity-80'
                    }`}
                  >
                    {meta.icon}
                    <span>{FEEDBACK_CATEGORY_LABELS[cat]}</span>
                  </button>
                );
              })}
            </div>
            {fieldErrors.category && (
              <p className="text-xs text-red-400 mt-1 mb-3">{fieldErrors.category}</p>
            )}

            {/* Star rating */}
            <label className="block text-sm font-medium text-[#E5C07B] mt-4 mb-2">
              How would you rate your experience? <span className="text-[#E5C07B]/60">(optional)</span>
            </label>
            <StarRating
              rating={rating}
              hoveredStar={hoveredStar}
              onRate={setRating}
              onHover={setHoveredStar}
              onLeave={() => setHoveredStar(0)}
            />

            {/* Message */}
            <label className="block text-sm font-medium text-[#E5C07B] mt-4 mb-2">
              Your feedback <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <textarea
                value={message}
                onChange={(e) => {
                  setMessage(e.target.value);
                  setFieldErrors((prev) => ({ ...prev, message: undefined }));
                }}
                maxLength={MAX_LENGTHS.feedbackMessage}
                rows={4}
                placeholder="Tell us what's on your mind..."
                className={`w-full px-3 py-2 bg-[#0D1B2A] border rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#00C2A8] focus:border-[#00C2A8] resize-none placeholder-[#E5C07B]/50 ${
                  fieldErrors.message ? 'border-red-500' : 'border-[#243B55]'
                }`}
              />
              <span className="absolute bottom-2 right-3 text-xs text-[#E5C07B]/60">
                {message.length}/{MAX_LENGTHS.feedbackMessage}
              </span>
            </div>
            {fieldErrors.message && (
              <p className="text-xs text-red-400 mt-1">{fieldErrors.message}</p>
            )}

            {/* Submit */}
            <div className="mt-5 flex justify-end space-x-3">
              <button
                onClick={handleClose}
                className="px-4 py-2 text-sm text-[#E5C07B] hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="bg-[#00C2A8] text-[#0D1B2A] font-semibold px-5 py-2 rounded-lg text-sm hover:bg-[#009987] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {submitting && (
                  <svg className="animate-spin h-4 w-4 text-[#0D1B2A]" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                )}
                <span>{submitting ? 'Submitting...' : 'Submit Feedback'}</span>
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
